import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Favorite as FavoriteIcon,
  Category as CategoryIcon,
  AccessTime as TimeIcon,
} from '@mui/icons-material';
import api from '../utils/authUtils';

function SearchPrayers() {
  const [searchQuery, setSearchQuery] = useState('');
  const [category, setCategory] = useState('');
  const [sortBy, setSortBy] = useState('relevance');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  const categories = ['General', 'Health', 'Family', 'Work', 'Financial', 'Relationships', 'Other'];

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      showSnackbar('Please enter a search query', 'error');
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams({
        q: searchQuery,
        category: category || '',
        sort_by: sortBy,
      });

      const response = await api.get(`/prayers/search?${params.toString()}`);
      setResults(response.data.prayers || []);
      showSnackbar(`Found ${response.data.prayers.length} prayers`, 'success');
    } catch (error) {
      console.error('Error searching prayers:', error);
      showSnackbar('Failed to search prayers', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCollectPrayer = async (prayerId) => {
    try {
      // Get user's current location
      if (!navigator.geolocation) {
        showSnackbar('Geolocation is not supported by your browser', 'error');
        return;
      }

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            await api.post(`/prayers/${prayerId}/collect`, {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
            });
            showSnackbar('Prayer collected successfully!', 'success');
            // Remove from results
            setResults(results.filter(p => p.id !== prayerId));
          } catch (error) {
            console.error('Error collecting prayer:', error);
            showSnackbar(error.response?.data?.message || 'Failed to collect prayer', 'error');
          }
        },
        (error) => {
          showSnackbar('Failed to get location. Please enable location services.', 'error');
        }
      );
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleClear = () => {
    setSearchQuery('');
    setCategory('');
    setSortBy('relevance');
    setResults([]);
  };

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return 'just now';
      if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
      if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
      if (diffDays < 30) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
      return date.toLocaleDateString();
    } catch {
      return new Date(dateString).toLocaleDateString();
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Search Prayers
        </Typography>

        {/* Search Filters */}
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                label="Search prayers"
                placeholder="Enter keywords..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                InputProps={{
                  endAdornment: searchQuery && (
                    <IconButton size="small" onClick={() => setSearchQuery('')}>
                      <ClearIcon />
                    </IconButton>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={category}
                  label="Category"
                  onChange={(e) => setCategory(e.target.value)}
                >
                  <MenuItem value="">All Categories</MenuItem>
                  {categories.map(cat => (
                    <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortBy}
                  label="Sort By"
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <MenuItem value="relevance">Relevance</MenuItem>
                  <MenuItem value="date">Newest First</MenuItem>
                  <MenuItem value="date_asc">Oldest First</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={2}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<SearchIcon />}
                  onClick={handleSearch}
                  disabled={loading}
                >
                  Search
                </Button>
                {results.length > 0 && (
                  <IconButton onClick={handleClear} color="error">
                    <ClearIcon />
                  </IconButton>
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Loading */}
        {loading && (
          <Box display="flex" justifyContent="center" my={4}>
            <CircularProgress />
          </Box>
        )}

        {/* Results */}
        {!loading && results.length > 0 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Search Results ({results.length})
            </Typography>
            <Grid container spacing={2}>
              {results.map((prayer) => (
                <Grid item xs={12} sm={6} md={4} key={prayer.id}>
                  <Card elevation={2}>
                    <CardContent>
                      <Box sx={{ mb: 1 }}>
                        <Chip
                          icon={<CategoryIcon />}
                          label={prayer.category || 'General'}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </Box>

                      <Typography variant="body1" sx={{ mb: 2 }}>
                        {prayer.content.length > 150
                          ? `${prayer.content.substring(0, 150)}...`
                          : prayer.content}
                      </Typography>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <TimeIcon fontSize="small" color="action" />
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(prayer.created_at)}
                        </Typography>
                      </Box>

                      <Typography variant="caption" color="text.secondary">
                        By: {prayer.is_anonymous ? 'Anonymous' : prayer.username}
                      </Typography>

                      {prayer.rank && (
                        <Typography variant="caption" color="primary" sx={{ display: 'block', mt: 0.5 }}>
                          Relevance: {(prayer.rank * 100).toFixed(1)}%
                        </Typography>
                      )}
                    </CardContent>

                    <CardActions>
                      <Button
                        size="small"
                        startIcon={<FavoriteIcon />}
                        onClick={() => handleCollectPrayer(prayer.id)}
                        color="primary"
                      >
                        Collect Prayer
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* No Results */}
        {!loading && results.length === 0 && searchQuery && (
          <Alert severity="info">
            No prayers found matching your search. Try different keywords or filters.
          </Alert>
        )}

        {/* Initial State */}
        {!loading && results.length === 0 && !searchQuery && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <SearchIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              Enter keywords to search for prayers
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Use filters to refine your search
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default SearchPrayers;
